import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ICtaOption } from '@campaign-form/form-fields/creators-and-ctas/cta-select.interface';
import { CtaSelectService } from '@campaign-form/form-fields/creators-and-ctas/cta-select.service';
import { DeliverableFlowDialogStore } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-flow-dialog.store';
import { DeliverableConfigurationStore } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-configuration.store';
import { IDeliverableConfigurationApi } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-list.interface';
import { EPlatformFeature } from '@enums/campaign.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ICampaignCreatorForm, ICtaForm } from '@pages/campaigns/campaign-form/campaign-form.interface';
import { IChannel } from '@pages/creators/creators.interface';
import { TGlobalTemplateApi } from '@pages/settings/template-settings/template-settings.interface';
import { AddPlatformInputComponent } from '@shared/components/add-platform-input/add-platform-input.component';
import { ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { SocialBadgeComponent } from '@shared/components/social-badge/social-badge.component';
import { ALL_CREATORS_ID, ALL_CREATORS_OPTION } from '@shared/constants/all-creators.constants';
import { TextClampTooltipDirective } from '@shared/directives/text-clamp-tooltip.directive';
import { camelCase } from 'lodash';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { PLATFORM_FEATURE_WARNINGS } from './platform-feature-warnings.constants';

@Component({
  imports: [
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    MatMenuModule,
    TranslateModule,
    SocialBadgeComponent,
    NgxSkeletonLoaderModule,
    IconComponent,
    AddPlatformInputComponent,
    TextClampTooltipDirective,
  ],
  providers: [DeliverableConfigurationStore],
  selector: 'app-platform-step',
  styleUrls: ['./platform-step.component.scss'],
  templateUrl: './platform-step.component.html',
})
export class PlatformStepComponent {
  public readonly campaignCreatorsValue = input.required<FormGroup<ICampaignCreatorForm>['value'][]>();
  public readonly ctaForm = input<FormGroup<ICtaForm> | null>(null);
  public readonly selectedCreatorIds = input.required<Set<string>>();

  public readonly selectedPlatformFeature = input<EPlatformFeature | null>(null);

  public readonly platformFeatureChanged = output<EPlatformFeature | null>();
  public readonly deliverableConfigurationsChanged = output<IDeliverableConfigurationApi[] | null>();
  public readonly globalTemplateSelected = output<string>();
  public readonly newPlatformAddedCreatorIds = output<Set<string>>();

  public readonly selectedCreators = computed(
    () =>
      Array.from(this.selectedCreatorIds()).map(
        (id) =>
          (id === ALL_CREATORS_ID ? ALL_CREATORS_OPTION(this.translate) : null) ??
          this.campaignCreatorsValue().find((cc) => cc.creatorId === id)?.creator!,
      ) ?? [],
  );
  public readonly deliverableConfigurations = computed(() => {
    const configurations = this.deliverableConfigurationStore.configurations$();
    this.deliverableConfigurationsChanged.emit(configurations);

    return configurations;
  });
  public readonly validPlatforms = computed(() => this.deliverableConfigurationStore.validPlatforms$());
  public readonly availableCtaOptions = computed(() =>
    this.ctaSelectService.getAvailableCtaOptions(
      this.deliverableConfigurations(),
      this.selectedCreators(),
      this.validPlatforms(),
    ),
  );
  public readonly globalTemplates = computed(() => this.deliverableFlowDialogStore.templates$() ?? []);
  public readonly isGlobalTemplatesLoading = computed(() => this.deliverableFlowDialogStore.loading$());
  public readonly filteredGlobalTemplates = computed(() => {
    const isAllCreatorsSelected =
      this.ctaForm()?.controls?.isAllCreators?.value ?? this.selectedCreatorIds().has(ALL_CREATORS_ID);
    const allowedPlatformFeatures = new Set(this.availableCtaOptions().map((ctaOption) => ctaOption.platformFeature));
    const creatorScopedTemplates = isAllCreatorsSelected
      ? this.globalTemplates()
      : this.globalTemplates().filter((template) =>
          allowedPlatformFeatures.has(camelCase(template.platformFeature ?? '') as EPlatformFeature),
        );
    const templateSearchTerm = this.templateSearchTerm().trim().toLowerCase();

    if (!templateSearchTerm) {
      return creatorScopedTemplates;
    }

    return creatorScopedTemplates.filter((template) => {
      const templateName = template.name.toLowerCase();
      const platformFeatureLabel = (
        this.translate.instant(`shared.platformFeatures.${template.platformFeature}`) as string
      ).toLowerCase();

      return templateName.includes(templateSearchTerm) || platformFeatureLabel.includes(templateSearchTerm);
    });
  });

  public readonly platformFeatureForm = new FormGroup({
    platformFeature: new FormControl<ICtaOption | null>(null, {
      validators: [Validators.required],
    }),
  });
  public readonly templateSearchTerm = signal<string>('');

  public readonly ALL_CREATORS_ID = ALL_CREATORS_ID;

  private readonly deliverableConfigurationStore = inject(DeliverableConfigurationStore);
  private readonly deliverableFlowDialogStore = inject(DeliverableFlowDialogStore);
  private readonly ctaSelectService = inject(CtaSelectService);
  private readonly dialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);

  private readonly newPlatformCreatorIds = new Set<string>();

  constructor() {
    this.deliverableConfigurationStore.fetchAvailableConfigurations();
    this.deliverableConfigurationStore.fetchValidPlatforms();
    this.createSelectedPlatformFeatureEffect();
  }

  public onPlatformSelected(ctaOption: ICtaOption): void {
    this.platformFeatureChanged.emit(ctaOption.platformFeature);
  }

  public getPlatformFeatureWarnings(): string[] {
    const platformFeature = this.platformFeatureForm.controls.platformFeature.value?.platformFeature ?? null;

    return platformFeature ? (PLATFORM_FEATURE_WARNINGS[platformFeature] ?? []) : [];
  }

  public onGlobalTemplateSelected(template: TGlobalTemplateApi): void {
    const normalizedPlatformFeature = camelCase(template.platformFeature ?? '') as EPlatformFeature;
    const selectedCtaOption = CtaSelectService.allCtaOptions[normalizedPlatformFeature];

    if (!selectedCtaOption) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        cancelText: this.translate.instant('pages.campaignForm.mergeTemplateConfirmDialog.cancel'),
        confirmText: this.translate.instant('pages.campaignForm.mergeTemplateConfirmDialog.confirm'),
        message: this.translate.instant('pages.campaignForm.mergeTemplateConfirmDialog.message', {
          ctaName: template.name || this.translate.instant(`shared.platformFeatures.${template.platformFeature}`),
        }),
        swapButtons: false,
        title: this.translate.instant('pages.campaignForm.mergeTemplateConfirmDialog.title'),
      },
      width: '400px',
    });

    dialogRef.afterClosed().subscribe((isConfirmed: boolean) => {
      if (!isConfirmed) {
        return;
      }

      this.globalTemplateSelected.emit(template.id);
      this.templateSearchTerm.set('');
    });
  }

  public onTemplateSearchTermChange(event: Event): void {
    const target = event.target as HTMLInputElement | null;

    this.templateSearchTerm.set(target?.value ?? '');
  }

  public onCreatedChannel(channel: IChannel | null): void {
    if (channel && this.selectedCreatorIds().size === 1 && !this.selectedCreatorIds().has(ALL_CREATORS_ID)) {
      this.campaignCreatorsValue()
        .find((cc) => cc.creatorId === this.selectedCreators()[0]?.id!)
        ?.creator?.channels?.push(channel);
      this.newPlatformCreatorIds.add(this.selectedCreators()[0]?.id!);
      this.newPlatformAddedCreatorIds.emit(this.newPlatformCreatorIds);
    }

    this.deliverableConfigurationStore.fetchAvailableConfigurations();
  }

  private createSelectedPlatformFeatureEffect(): void {
    effect(() => {
      const platformFeature = this.selectedPlatformFeature() ?? this.ctaForm()?.controls.platformFeature.value ?? null;

      if (!platformFeature) {
        this.platformFeatureForm.controls.platformFeature.setValue(null, { emitEvent: false });

        return;
      }

      const option = CtaSelectService.allCtaOptions[platformFeature];

      if (!option) {
        return;
      }

      this.platformFeatureForm.controls.platformFeature.setValue(option, { emitEvent: false });
    });
  }
}
